import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { FinancialRow } from "@/pages/finance/FinanceDashboard";

function useResizeObserver<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const RO: typeof ResizeObserver | undefined = (window as any).ResizeObserver;
    if (!RO) {
      const id = window.setInterval(() => {
        const cr = el.getBoundingClientRect();
        setSize({ width: cr.width, height: cr.height });
      }, 300);
      return () => clearInterval(id);
    }

    const obs = new RO((entries: ResizeObserverEntry[]) => {
      const cr = entries[0]?.contentRect;
      if (cr) setSize({ width: cr.width, height: cr.height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return { ref, size };
}

interface BarChartProps {
  data: FinancialRow[];
  color: string;
}

function BarChart({ data, color }: BarChartProps) {
  const { ref, size } = useResizeObserver<HTMLDivElement>();
  const tipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    d3.select(root).selectAll("*").remove();

    const width = Math.max(0, size.width);
    const height = Math.max(0, size.height);

    if (!width || !height) return;

    if (!data.length) {
      d3.select(root)
        .append("div")
        .attr("class", "w-full h-full grid place-items-center text-muted-foreground")
        .text("No data available");
      return;
    }

    const margin = { top: 16, right: 20, bottom: 40, left: 56 };
    const innerW = Math.max(0, width - margin.left - margin.right);
    const innerH = Math.max(0, height - margin.top - margin.bottom);

    const svg = d3.select(root).append("svg").attr("width", width).attr("height", height);
    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.Date))
      .range([0, innerW])
      .padding(0.15);

    const maxY = Math.max(0, d3.max(data, (d) => d.Value) ?? 0);
    const y = d3.scaleLinear().domain([0, maxY * 1.2]).nice().range([innerH, 0]);

    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .call(d3.axisBottom(x).tickSizeOuter(0))
      .selectAll("text")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("font-size", "12px");

    const bars = g
      .selectAll("rect")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", (d) => x(d.Date)!)
      .attr("y", (d) => y(d.Value))
      .attr("width", x.bandwidth())
      .attr("height", (d) => Math.max(0, innerH - y(d.Value)))
      .attr("fill", color)
      .attr("rx", 4)
      .attr("ry", 4);

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    const tip = tipRef.current || document.createElement("div");
    tip.className =
      "fixed pointer-events-none bg-gray-900 text-white text-sm px-3 py-2 rounded opacity-0 transition-opacity z-50";
    if (!tipRef.current) {
      document.body.appendChild(tip);
      tipRef.current = tip;
    }

    const showTip = (evt: MouseEvent, d: FinancialRow) => {
      tip.innerHTML = `
        <div class="font-semibold">${d.Date}</div>
        <div>Value: ${formatter.format(d.Value)}</div>
        <div>Category: ${d.Category ?? "-"}</div>`;
      tip.style.left = `${evt.pageX + 10}px`;
      tip.style.top = `${evt.pageY - 28}px`;
      tip.style.opacity = "1";
    };
    const hideTip = () => (tip.style.opacity = "0");

    bars
      .on("mousemove", function (evt, d) {
        showTip(evt as unknown as MouseEvent, d as FinancialRow);
      })
      .on("mouseleave", hideTip);

    return () => {
      d3.select(root).selectAll("*").remove();
      if (tipRef.current) tipRef.current.style.opacity = "0";
    };
  }, [data, size, color]);

  return <div ref={ref} className="w-full h-full" />;
}

interface FinancialChartsProps {
  rows: FinancialRow[];
}

export default function FinancialCharts({ rows }: FinancialChartsProps) {
  const revenue = useMemo(() => rows.filter((r) => r.Category === "Revenue"), [rows]);
  const expenses = useMemo(() => rows.filter((r) => r.Category === "Expenses"), [rows]);
  const balance = useMemo(
    () => rows.filter((r) => r.Category === "Assets" || r.Category === "Liabilities"),
    [rows]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Revenue (P&L)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[380px]">
            <BarChart data={revenue} color="#16a34a" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Expenses (P&L)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[380px]">
            <BarChart data={expenses} color="#dc2626" />
          </div>
        </CardContent>
      </Card>

      <div className="md:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>Assets and Liabilities (Balance Sheet)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[380px]">
              <BarChart data={balance} color="#2563eb" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
